import normalizeWhitespace from "@next-platform/client-common-core/utils/normalizeWhitespace";
import { LorebookVisibility } from "@next-platform/packages-ts-types";
import { z } from "zod";

import {
  ENTRY_FORM_FIELDS,
  FORM_FIELDS,
  MAX_ENTRY_CONTENT_LENGTH,
  MAX_ENTRY_NAME_LENGTH,
  MAX_LOREBOOK_DESCRIPTION_LENGTH,
  MAX_LOREBOOK_NAME_LENGTH,
  MIN_FIELD_LENGTH,
} from "./constants";
import { ValidationSchemaT } from "./types";
import getLorebookRefineTags from "./utils/getLorebookRefineTags";

const getTextFieldSchema = (translate: ValidationSchemaT, maxLength: number) =>
  z
    .string()
    .min(1, translate("lorebook:validation.field.required"))
    .transform(normalizeWhitespace)
    .pipe(
      z
        .string()
        .min(MIN_FIELD_LENGTH, translate("lorebook:validation.field.minLength", { min: MIN_FIELD_LENGTH }))
        .max(maxLength, translate("lorebook:validation.field.maxLength", { max: maxLength })),
    );

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export function getBaseLorebookSchema(t: ValidationSchemaT) {
  return {
    [FORM_FIELDS.NAME]: getTextFieldSchema(t, MAX_LOREBOOK_NAME_LENGTH),
    [FORM_FIELDS.DESCRIPTION]: getTextFieldSchema(t, MAX_LOREBOOK_DESCRIPTION_LENGTH),
    [FORM_FIELDS.AVATAR]: z.string().min(1, t("lorebook:validation.field.required")),
    [FORM_FIELDS.VISIBILITY]: z.nativeEnum(LorebookVisibility),
    [FORM_FIELDS.TAGS]: z.array(z.string().min(1)).optional(),
  };
}

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getLorebookCreateValidationSchema = (t: ValidationSchemaT) =>
  z
    .object({
      ...getBaseLorebookSchema(t),
      [FORM_FIELDS.GUIDELINES]: z.boolean().refine((value: boolean) => value === true, {
        message: t("lorebook:validation.agree.required"),
      }),
    })
    .superRefine(getLorebookRefineTags(t));

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getLorebookEditValidationSchema = (t: ValidationSchemaT) => z.object(getBaseLorebookSchema(t)).superRefine(getLorebookRefineTags(t));

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
export const getEntryValidationSchema = (t: ValidationSchemaT) =>
  z.object({
    [ENTRY_FORM_FIELDS.NAME]: getTextFieldSchema(t, MAX_ENTRY_NAME_LENGTH),
    [ENTRY_FORM_FIELDS.KEYWORDS]: z
      .array(z.string().min(MIN_FIELD_LENGTH, t("lorebook:validation.field.minLength", { min: MIN_FIELD_LENGTH })))
      .min(1, t("lorebook:validation.entry.keywords.required")),
    [ENTRY_FORM_FIELDS.CONTENT]: getTextFieldSchema(t, MAX_ENTRY_CONTENT_LENGTH),
  });
